import React from "react";
import { Show } from "@refinedev/antd";
import { useDelete, useShow } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Descriptions,
  Empty,
  Image,
  Button,
  message,
  Modal,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import type { ApiError } from "../../api-client";
import { CampaignsService, type CampaignResponse } from "../../api-client";
import { CampaignStatsCard } from "./components/CampaignStatsCard";
import { CreateTaskModal } from "./components/CreateTaskModal";
import { TasksTable } from "./components/TasksTable";

const { TabPane } = Tabs;
const { Text, Paragraph } = Typography;

const statusColorMap: Record<CampaignResponse["status"], string> = {
  draft: "default",
  active: "processing",
  done: "success",
  cancelled: "warning",
  failed: "error",
};

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "—";

export const CampaignShow: React.FC = () => {
  const { query } = useShow<CampaignResponse>();
  const navigate = useNavigate();
  const { mutate: deleteCampaign, isLoading: isDeleting } =
    useDelete<CampaignResponse>();
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = React.useState(false);
  const [activeTasksRefreshKey, setActiveTasksRefreshKey] = React.useState(0);
  const [isActivating, setIsActivating] = React.useState(false);
  const [isCancelling, setIsCancelling] = React.useState(false);
  const isLoading = query.isLoading;
  const campaign = query.data?.data;
  const campaignId = campaign?.id;

  if (query.isError) {
    return (
      <Show title="Кампания" breadcrumb={<div>Кампании / Информация</div>}>
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Не удалось загрузить данные кампании"
          description="Проверьте подключение или попробуйте обновить страницу."
          action={
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => query.refetch?.()}>
                Повторить
              </Button>
              <Button type="primary" onClick={() => navigate("/campaigns")}>
                Вернуться к списку
              </Button>
            </Space>
          }
        />
      </Show>
    );
  }

  const handleManualTaskSuccess = () => {
    setActiveTasksRefreshKey((prev) => prev + 1);
    query?.refetch();
  };

  const renderCampaignMissingState = (description = "Кампания не найдена") => (
    <div style={{ textAlign: "center", padding: "48px 0" }}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div>{description}</div>
            <Text type="secondary" style={{ margin: 0 }}>
              Возможно, кампания была удалена или у вас нет к ней доступа
            </Text>
          </div>
        }
      >
        <Button
          type="primary"
          onClick={() => navigate("/campaigns")}
          aria-label="Вернуться к списку кампаний"
        >
          Вернуться к списку
        </Button>
      </Empty>
    </div>
  );

  const handleEdit = () => {
    if (!campaign?.id) {
      return;
    }

    navigate(`/campaigns/edit/${campaign.id}`);
  };

  const handleDelete = () => {
    if (!campaign?.id) {
      return;
    }

    Modal.confirm({
      title: "Удалить кампанию?",
      content: "Кампания будет удалена без возможности восстановления.",
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true, loading: isDeleting },
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          deleteCampaign(
            {
              resource: "campaigns",
              id: campaign.id,
            },
            {
              onSuccess: () => {
                message.success("Кампания удалена");
                navigate("/campaigns");
                resolve();
              },
              onError: (error) => {
                console.error("Delete campaign error", error);
                message.error("Не удалось удалить кампанию");
                reject(error);
              },
            },
          );
        }),
    });
  };

  const handleCancel = () => {
    if (!campaign?.id) {
      return;
    }

    Modal.confirm({
      title: "Отменить кампанию?",
      content: "Статус кампании будет изменен на 'cancelled'.",
      okText: "Отменить",
      cancelText: "Отмена",
      okButtonProps: { danger: true, loading: isCancelling },
      onOk: async () => {
        setIsCancelling(true);
        try {
          await CampaignsService.cancelCampaignApiCampaignsCampaignIdCancelPost({
            campaignId: campaign.id as string,
          });
          message.success("Кампания отменена");
          await query?.refetch();
        } catch (error) {
          console.error("Cancel campaign error", error);
          const apiError = error as ApiError | undefined;
          const errorMessage =
            (apiError?.body as { detail?: string } | undefined)?.detail ??
            apiError?.message ??
            "Не удалось отменить кампанию";
          message.error(errorMessage);
          throw error;
        } finally {
          setIsCancelling(false);
        }
      },
    });
  };

  const handleActivate = async () => {
    if (!campaign?.id) {
      return;
    }

    setIsActivating(true);

    try {
      await CampaignsService.activateCampaignApiCampaignsCampaignIdActivatePost({
        campaignId: campaign.id,
      });

      message.success("Кампания активирована");
      await query?.refetch();
    } catch (error) {
      console.error("Activate campaign error", error);
      const apiError = error as ApiError | undefined;
      const errorMessage =
        (apiError?.body as { detail?: string } | undefined)?.detail ??
        apiError?.message ??
        "Не удалось активировать кампанию";

      message.error(errorMessage);
    } finally {
      setIsActivating(false);
    }
  };

  const renderActionButtons = () => {
    if (!campaign?.id) {
      return null;
    }

    if (campaign.status === "draft") {
      return (
        <Space>
          <Button
            type="primary"
            onClick={handleActivate}
            loading={isActivating}
            aria-label="Активировать кампанию"
          >
            Активировать
          </Button>
          <Button onClick={handleEdit}>Редактировать</Button>
          <Button
            danger
            onClick={handleDelete}
            loading={isDeleting}
            aria-label="Удалить кампанию"
          >
            Удалить
          </Button>
        </Space>
      );
    }

    if (campaign.status === "active") {
      return (
        <Button
          danger
          onClick={handleCancel}
          loading={isCancelling}
          aria-label="Отменить кампанию"
        >
          Отменить
        </Button>
      );
    }

    return null;
  };

  const renderCampaignDescriptions = () => {
    if (isLoading) {
      return (
        <Descriptions column={1} bordered size="middle">
          <Descriptions.Item label="Название">
            <Skeleton.Input active style={{ width: 220 }} />
          </Descriptions.Item>
          <Descriptions.Item label="Тип кампании">
            <Skeleton.Input active style={{ width: 160 }} />
          </Descriptions.Item>
          <Descriptions.Item label="Статус">
            <Skeleton.Button active size="small" style={{ width: 120 }} />
          </Descriptions.Item>
          <Descriptions.Item label="Создана">
            <Skeleton.Input active style={{ width: 180 }} />
          </Descriptions.Item>
          <Descriptions.Item label="Запущена">
            <Skeleton.Input active style={{ width: 180 }} />
          </Descriptions.Item>
          <Descriptions.Item label="Завершена">
            <Skeleton.Input active style={{ width: 180 }} />
          </Descriptions.Item>
          <Descriptions.Item label="Шаблон сообщения">
            <Skeleton active paragraph={{ rows: 3 }} title={false} />
          </Descriptions.Item>
          <Descriptions.Item label="Изображение">
            <Skeleton.Avatar active shape="square" size={96} />
          </Descriptions.Item>
          <Descriptions.Item label="Автогенерация задач">
            <Skeleton.Button active size="small" style={{ width: 100 }} />
          </Descriptions.Item>
        </Descriptions>
      );
    }

    if (!campaign) {
      return null;
    }

    return (
      <Descriptions column={1} bordered size="middle">
        <Descriptions.Item label="Название">
          <Text strong>{campaign.title}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Тип кампании">
          <Text>{campaign.rule_code}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Статус">
          <Tag
            color={statusColorMap[campaign.status] ?? "default"}
            icon={
              campaign.status === "failed" ? (
                <ExclamationCircleOutlined />
              ) : undefined
            }
          >
            {campaign.status}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Создана">
          <Text>{formatDate(campaign.created_at)}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Запущена">
          <Text>{formatDate(campaign.started_at)}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Завершена">
          <Text>{formatDate(campaign.finished_at)}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Шаблон сообщения">
          {campaign.message_template ? (
            <Paragraph
              ellipsis={{ rows: 3, expandable: true, symbol: "Показать больше" }}
              style={{ marginBottom: 0 }}
            >
              {campaign.message_template}
            </Paragraph>
          ) : (
            <Text type="secondary">Шаблон не задан</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Изображение">
          {campaign.image_data ? (
            <Image
              src={campaign.image_data}
              alt={`Изображение кампании: ${campaign.title ?? "без названия"}`}
              width={180}
              style={{ borderRadius: 8 }}
            />
          ) : (
            <Text type="secondary">Изображение не загружено</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Автогенерация задач">
          <Tag color={campaign.auto_generate_tasks ? "success" : "default"}>
            {campaign.auto_generate_tasks ? "Да" : "Нет"}
          </Tag>
        </Descriptions.Item>
        {campaign.status === "failed" && campaign.error ? (
          <Descriptions.Item label="Ошибка">
            <Alert
              type="error"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message="Кампания завершилась с ошибкой"
              description={campaign.error}
              style={{ marginBottom: 0 }}
            />
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    );
  };

  if (!isLoading && !campaign) {
    return renderCampaignMissingState();
  }

  return (
    <Show
      title={campaign ? `Кампания: ${campaign.title}` : "Кампания"}
      breadcrumb={<div>Кампании / Информация</div>}
    >
      <Card>
        <Tabs defaultActiveKey="overview">
          <TabPane tab="Обзор" key="overview">
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="primary"
                  onClick={() => setIsCreateTaskModalOpen(true)}
                  disabled={!campaignId || isLoading}
                >
                  Создать задачу вручную
                </Button>
              </div>
              <Card
                type="inner"
                title="Информация о кампании"
                bordered={false}
                extra={renderActionButtons()}
              >
                {renderCampaignDescriptions()}
              </Card>
              {campaign?.id ? (
                <CampaignStatsCard
                  campaignId={campaign.id}
                  refreshKey={0}
                  status={campaign.status}
                />
              ) : null}
            </Space>
          </TabPane>

          <TabPane tab="Активные задачи" key="active">
            <Card type="inner" title="Активные задачи" bordered={false}>
              {campaignId ? (
                <TasksTable
                  key={`active-${activeTasksRefreshKey}`}
                  campaignId={campaignId}
                  archived={false}
                  showUserLink
                />
              ) : (
                renderCampaignMissingState()
              )}
            </Card>
          </TabPane>

          <TabPane tab="Архив задач" key="archived">
            <Card type="inner" title="Архив задач" bordered={false}>
              {campaignId ? (
                <TasksTable campaignId={campaignId} archived showUserLink />
              ) : (
                renderCampaignMissingState()
              )}
            </Card>
          </TabPane>
        </Tabs>
      </Card>

      {campaignId ? (
        <CreateTaskModal
          campaignId={campaignId}
          visible={isCreateTaskModalOpen}
          onClose={() => setIsCreateTaskModalOpen(false)}
          onSuccess={handleManualTaskSuccess}
          defaultTemplate={campaign.message_template ?? undefined}
          campaignImageData={campaign.image_data}
        />
      ) : null}
    </Show>
  );
};

export default CampaignShow;
